/**
 * A navegação da plataforma — rótulo, ícone e onde o item mora.
 *
 * Vive aqui, e não no layout, porque duas partes precisam dela e só uma delas é
 * React: a tela desenha o menu, e o Lucas ensina o caminho até ele. Enquanto os
 * nomes eram escritos à mão do lado do Lucas, ele mandou gente procurar
 * "Templates" no menu lateral (não existe), chamou o Kanban de "CRM" e disse que
 * Disparos ficava dentro do Inbox. Três vezes o mesmo defeito: uma cópia que
 * envelhece em silêncio.
 *
 * `icon` é o NOME do ícone, não o componente — a API lê este arquivo e lá não
 * existe React. A resolução para componente mora no layout e no cartão de
 * caminho, cada um com o seu mapa.
 *
 * As regras de visibilidade (`minLevel`, `feature`, `permission`) continuam
 * aqui porque descrevem o item, não a renderização: quem não enxerga um item
 * não deve ser mandado até ele.
 *
 * `embed` é o eixo que o shell embutido (EMB-12) usa para decidir o que
 * aparece em `/embed/*` — mesmo princípio dos outros três: uma flag no
 * catálogo, nunca um segundo menu escrito à mão no layout.
 *
 * `requiresPartner` (PCA-11) é o eixo mais novo, pela mesma razão: o item só
 * aparece para contas que de fato são parceiras.
 */

/** Itens da lista principal, na ordem em que aparecem. */
export const MAIN_NAV = Object.freeze([
  { key: "dashboard", icon: "LayoutDashboard", label: "Dashboard", path: "", minLevel: 0, embed: true },
  { key: "inbox", icon: "Inbox", label: "Inbox", path: "/inbox", badge: true, minLevel: 10, embed: true },
  // Kanban: ícone sempre visível (sem `feature` e sem `permission`) para
  // maximizar descoberta da feature. O FeaturePageGate exibe o upgrade panel
  // quando crm.enabled = false; o PermissionRequestGate orienta o pedido de
  // acesso quando o usuário não possui crm.read.
  { key: "crm", icon: "Kanban", label: "Kanban", path: "/crm", minLevel: 0, embed: true },
  // Canais subiu do menu do avatar para cá: conectar o WhatsApp é o primeiro
  // passo de toda conta nova, e ninguém procura o primeiro passo dentro do
  // próprio perfil. Quem trocou de lugar com ele foi a Base de Conhecimento,
  // que só é mexida depois que a conta já atende.
  { key: "channels", icon: "Smartphone", label: "Canais", path: "/channels", minLevel: 50, embed: true },
  {
    key: "reports",
    icon: "ChartNoAxesCombinedIcon",
    label: "Relatórios",
    path: "/reports",
    minLevel: 30,
    embed: true,
  },
]);

export const CORE_NAV = Object.freeze([
  { key: "automation", icon: "Workflow", label: "Automações", path: "/automation", minLevel: 50, embed: true },
  { key: "broadcasts", icon: "Megaphone", label: "Disparos", path: "/broadcasts", minLevel: 30, embed: true },
  // Integrações também saiu do menu do avatar. Ela é irmã de Automações e
  // Disparos — coisas que a conta liga uma vez e volta a mexer —, não uma
  // preferência pessoal, que é o que o menu do avatar reúne.
  { key: "integrations", icon: "Waypoints", label: "Integrações", path: "/integrations", minLevel: 50, embed: true },
  // Indique e Ganhe. Fica em CORE porque é da conta, não do atendente: quem
  // compartilha o link e recebe o crédito na fatura é quem responde pelo
  // contrato. `embed: false` porque o programa é da TouchWorks com o cliente
  // direto — uma conta filha de parceiro não indica ninguém (ela sequer passa
  // pelo checkout do site, que é onde o cookie do link é lido).
  //
  // `flat: true` porque a rota (`/internal/indicacoes`) é a única do catálogo
  // que NÃO mora sob `implementation/:clientId` — de propósito, a tela sempre
  // mostra a indicação da conta da SESSÃO (`req.user.accountId`), nunca a
  // conta que um superadmin esteja navegando por `:clientId`. Sem esta flag,
  // `companyPath()` monta `/internal/implementation/:id/indicacoes`, que não
  // bate com nenhuma rota registrada — o clique navega e a tela some, sem
  // request nenhuma pra API (achado ao vivo: 2026-09-23).
  { key: "referrals", icon: "Gift", label: "Indique e Ganhe", path: "/indicacoes", minLevel: 30, embed: false, flat: true },
]);

export const TEAM_NAV = Object.freeze([
  { key: "users", icon: "Users", label: "Usuários", path: "/users", minLevel: 50, embed: true },
  {
    key: "departments",
    icon: "Building2",
    label: "Departamentos",
    path: "/departments",
    minLevel: 50,
    embed: true,
  },
  // Contas vinculadas da conta parceira (PCA-11). Fica com Usuários e
  // Departamentos porque responde a mesma pergunta: quem/o que pertence à
  // minha organização.
  //
  // `embed: false` é carregado de significado: o shell embutido é a superfície
  // white-label que o parceiro mostra às PRÓPRIAS contas filhas, e uma filha
  // nunca pode ver as irmãs ali.
  {
    key: "child_accounts",
    icon: "Handshake",
    label: "Contas",
    path: "/contas",
    minLevel: 50,
    requiresPartner: true,
    embed: false,
  },
]);

/**
 * Não ficam na lista lateral: abrem pelo menu do avatar, seção Configurações.
 * É por isso que "está no menu lateral" manda a pessoa procurar em vão.
 */
export const CONFIG_NAV = Object.freeze([
  {
    key: "knowledge_base",
    icon: "Database",
    label: "Base de Conhecimento",
    path: "/knowledge-base",
    minLevel: 10,
    feature: "rag.enabled",
    embed: true,
  },
]);

/**
 * Rodapé da lista lateral — hoje são gatilhos escritos à mão em
 * InternalLayout.jsx (sino de notificações, link "Guias de Uso"), não itens
 * data-driven como o resto do catálogo. Entram aqui só para que o eixo
 * `embed` tenha ONDE declarar "isto é visível no shell embutido" — o rótulo
 * abaixo é o que a UI de fato mostra hoje (não o nome coloquial do briefing
 * do parceiro, "Suporte"/"Notificações"), para não repetir o mesmo defeito
 * de drift que este arquivo existe para evitar.
 */
export const FOOTER_NAV = Object.freeze([
  { key: "notifications", icon: "Bell", label: "Notificações", path: "/notifications", minLevel: 0, embed: true },
  { key: "support", icon: "LifeBuoy", label: "Guias de Uso", path: "/support", minLevel: 50, embed: true },
]);

/**
 * Não ficam na lista lateral nem no menu do avatar: são abas dentro do modal
 * de Preferências, que abre pela engrenagem no topo da lista de atendimentos.
 * Telas de REGRA, e não de operação — quem as visita já está dentro do Inbox.
 */
export const PREFERENCES_NAV = Object.freeze([
  {
    key: "sales_intelligence",
    icon: "Sparkles",
    label: "Sales Intelligence",
    path: "/sales-intelligence",
    minLevel: 30,
    feature: "sales.enabled",
  },
]);

/**
 * Onde o item é encontrado, em palavras — entra na resposta ao usuário.
 *
 * Todo grupo de `GROUPS` precisa de uma entrada aqui: `fixNavPlace` escreve o
 * valor direto na frase, e um grupo sem lugar vira a palavra "null" no texto
 * que a pessoa lê.
 */
export const NAV_PLACE = Object.freeze({
  main: "Menu lateral",
  core: "Menu lateral",
  team: "Menu lateral",
  config: "Seu perfil (avatar no rodapé do menu)",
  preferences: "Inbox → engrenagem no topo da lista → Configurações gerais",
  footer: "Rodapé do menu lateral",
});

const GROUPS = Object.freeze([
  { group: "main", items: MAIN_NAV },
  { group: "core", items: CORE_NAV },
  { group: "team", items: TEAM_NAV },
  { group: "config", items: CONFIG_NAV },
  { group: "preferences", items: PREFERENCES_NAV },
  { group: "footer", items: FOOTER_NAV },
]);

/** Todos os itens, cada um sabendo em que grupo mora. */
export const NAV_ITEMS = Object.freeze(
  GROUPS.flatMap(({ group, items }) => items.map((item) => Object.freeze({ ...item, group }))),
);

const BY_KEY = new Map(NAV_ITEMS.map((item) => [item.key, item]));

/** @param {string} key @returns {object|null} */
export function navItem(key) {
  return BY_KEY.get(key) || null;
}

/**
 * O caminho até o item, escrito como a pessoa o percorre.
 * @param {string} key
 * @param {...string} rest — passos depois do item de menu
 */
export function navPath(key, ...rest) {
  const item = navItem(key);
  if (!item) return null;
  return [NAV_PLACE[item.group], item.label, ...rest].join(" → ");
}

/** @param {object} item @returns {boolean} true quando o item é visível no shell embutido (EMB-12). */
export function isEmbedVisible(item) {
  return item.embed === true;
}

/**
 * Filtra uma lista de itens do catálogo para o que o shell embutido pode
 * mostrar — mesma forma de `filterNavByLevel`/`filterNavByFeature` (que
 * vivem no layout, não aqui): recebe a lista já filtrada pelos outros eixos
 * e aplica só o eixo novo, nunca inventa um segundo menu.
 * @param {object[]} items
 * @returns {object[]}
 */
export function filterNavForEmbed(items) {
  return items.filter(isEmbedVisible);
}

/**
 * Filtra pelo eixo de conta parceira (PCA-11) — mesma forma de
 * `filterNavForEmbed`: recebe a lista já filtrada pelos outros eixos e aplica
 * só este.
 *
 * Existe separado de `feature` porque o sinal é outro: `filterNavByFeature`
 * resolve o item pelo mapa de entitlements e libera tudo em trial. Ser
 * parceiro é uma união de três sinais estruturais (AD-006) que chega pronta
 * como booleano — e sem atalho de trial, para que uma conta em trial que não é
 * parceira NÃO ganhe o item.
 *
 * @param {object[]} items
 * @param {boolean} isPartnerAccount
 * @returns {object[]}
 */
export function filterNavByPartner(items, isPartnerAccount) {
  if (isPartnerAccount === true) return items;
  return items.filter((item) => item.requiresPartner !== true);
}

export default NAV_ITEMS;
